import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createJob, updateJob, addJobApplicationSource, getAllJobApplicationSources, getJobApplicationSources } from "../api";
import { jobStatusOptions } from "../services/dropdownOptions";
import PopupMessage from "./PopupMessage";

const AddJobPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editJob = location.state?.mode === 'edit' ? location.state.job : null;
  const isEditMode = Boolean(editJob);

  const [formData, setFormData] = useState({
    title: editJob?.title || "",
    skill: editJob?.skill || "",
    experience_required: editJob?.experience_required || "",
    budget: editJob?.budget || "",
    job_description: editJob?.job_description || "",
    location: editJob?.location || "",
    job_type: editJob?.job_type || "full-time",
    status: editJob?.status || "open",
    company: editJob?.company || "",
    department: editJob?.department || "",
    remote_work: editJob ? (editJob.remote_work ? true : false) : false,
    closing_on: editJob?.closing_on ? editJob.closing_on.split('T')[0] : "",
    creator_comment: editJob?.creator_comment || ""
  });


  // Job source selection state
  const [showSourcePopup, setShowSourcePopup] = useState(false);
  const [showCreateSourcePopup, setShowCreateSourcePopup] = useState(false);
  const [availableSources, setAvailableSources] = useState([]);
  const createSourceInitialState = {
    SourceName: '', SourceType: '', JobLink: '', CompanyName: '', CompanyWebsite: '', CompanyLinkedIn: '', WhatsAppGroup: '', ContactPersonName: '', ContactPersonMobile: '', ContactPersonEmail: '', ContactPersonLinkedIn: '', SecondaryContactName: '', SecondaryContactMobile: '', SecondaryContactEmail: '', Country: '', State: '', City: '', SourceStatus: 'Active', Notes: '', LastContactDate: '', NextFollowUpDate: ''
  };
  const [createSourceFormData, setCreateSourceFormData] = useState(createSourceInitialState);
  const [sourceSearch, setSourceSearch] = useState("");
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceValidationVisible, setSourceValidationVisible] = useState(false);
  const [sourceValidationMessage, setSourceValidationMessage] = useState("");

  const [errors, setErrors] = useState({});
  // Remove sourceErrors, not needed for popup selection
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    if (name === 'remote_work') {
      newValue = value === 'yes';
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSourceSelect = (source) => {
    console.log('🧩 [UI] Source selected:', source);
    setSelectedSource(source);
  };

  const handleCreateSourceSubmit = async () => {
    // Client-side validation for required fields
    const missing = [];
    if (!createSourceFormData.SourceName || !createSourceFormData.SourceName.trim()) missing.push('Source Name');
    if (!createSourceFormData.SourceType || !createSourceFormData.SourceType.trim()) missing.push('Source Type');

    if (missing.length > 0) {
      const msg = `Please provide required field(s): ${missing.join(', ')}`;
      console.warn('🧩 [UI] Source validation failed:', msg);
      setSourceValidationMessage(msg);
      setSourceValidationVisible(true);
      return;
    }

    console.log('🧩 [UI] Add New Source submit, payload:', createSourceFormData);
    try {
      // prepare payload: convert empty date strings to null so backend accepts them as datetime NULL
      const payload = { ...createSourceFormData };
      if (!payload.LastContactDate || payload.LastContactDate === '') payload.LastContactDate = null;
      if (!payload.NextFollowUpDate || payload.NextFollowUpDate === '') payload.NextFollowUpDate = null;

      const response = await addJobApplicationSource(null, payload);
      console.log('🧩 [UI] addJobApplicationSource response:', response);
      if (response.success) {
        const all = await getAllJobApplicationSources();
        console.log('🧩 [UI] getAllJobApplicationSources response after create:', all);
        if (all.success) {
          setAvailableSources(Array.isArray(all.data) ? all.data : []);
          const createdId = response.data?.id;
          const created = (all.data || []).find(s => s.id === createdId);
          if (created) setSelectedSource(created);
        }
        setShowCreateSourcePopup(false);
        setCreateSourceFormData(createSourceInitialState);
      } else {
        console.error('Failed to create source', response.message);
        setSourceValidationMessage(response.message || 'Failed to create source');
        setSourceValidationVisible(true);
      }
    } catch (err) {
      console.error('Error creating source', err);
      setSourceValidationMessage('Network error: ' + (err.message || err));
      setSourceValidationVisible(true);
    }
  };

  // Validate form (job and source selection)
  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Job title is required";
    if (!formData.company.trim()) newErrors.company = "Company is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.skill.trim()) newErrors.skill = "Skills are required";
    if (!selectedSource) newErrors.source = "Please select a job source";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");
    if (!validateForm()) {
      setErrorMessage("Please fix the highlighted fields.");
      setShowErrorPopup(true);
      return;
    }
    setIsLoading(true);
    try {
      let jobId = null;
      let jobResponse;
      if (isEditMode) {
        jobResponse = await updateJob(editJob.job_id, { ...formData, source_id: selectedSource?.id });
        jobId = editJob.job_id;
      } else {
        console.log('🧩 [UI] createJob payload, selectedSource:', selectedSource);
        // Send id for source mapping
        jobResponse = await createJob({ ...formData, source_id: selectedSource?.id });
        jobId = jobResponse?.data?.job_id;
      }
      if (jobResponse.success && jobId) {
        setSuccessMessage(isEditMode ? "Job updated successfully!" : "Job created successfully!");
        setShowSuccessPopup(true);
        if (!isEditMode) {
          setFormData({
            title: "",
            skill: "",
            experience_required: "",
            budget: "",
            job_description: "",
            location: "",
            job_type: "full-time",
            status: "open",
            company: "",
            department: "",
            remote_work: false,
            closing_on: "",
            creator_comment: ""
          });
          setSelectedSource(null);
          setErrors({});
        }
        setTimeout(() => {
          navigate('/jobs');
        }, 1200);
      } else {
        setErrorMessage(jobResponse.message || (isEditMode ? "Failed to update job" : "Failed to create job"));
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error(isEditMode ? "Job update error:" : "Job creation error:", error);
      setErrorMessage("An error occurred. Please try again.");
      setShowErrorPopup(true);
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch available sources on mount
  React.useEffect(() => {
    const fetchSources = async () => {
      setSourceLoading(true);
      console.log('🧩 [UI] Fetching available sources for source popup');
      try {
        const response = await getAllJobApplicationSources();
        console.log('🧩 [UI] getAllJobApplicationSources response:', response);
        if (response.success) {
          setAvailableSources(Array.isArray(response.data) ? response.data : []);
        } else {
          console.error('🧩 [UI] Failed to fetch sources:', response.message);
          setAvailableSources([]);
        }
      } catch (err) {
        console.error('🧩 [UI] Error fetching sources:', err);
        setAvailableSources([]);
      } finally {
        setSourceLoading(false);
      }
    };

    if (showSourcePopup) {
      fetchSources();
    }
  }, [showSourcePopup]);

  // When editing a job, fetch the currently mapped source and set it
  React.useEffect(() => {
    const fetchMappedSource = async () => {
      if (!isEditMode) return;
      try {
        const res = await getJobApplicationSources(editJob.job_id);
        console.log('🧩 [UI] getJobApplicationSources for edit response:', res);
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          setSelectedSource(res.data[0]);
        }
      } catch (err) {
        console.error('🧩 [UI] Error fetching mapped source for edit:', err);
      }
    };
    fetchMappedSource();
  }, [isEditMode, editJob]);

  return (
    <div className="resume-page-wrapper">
      <div className="resume-container">
        {/* Header */}
        <div className="resume-header">
          <h1 className="resume-title">{isEditMode ? 'Edit Job' : 'Add New Job'}</h1>
          <p className="resume-subtitle">
            {isEditMode
              ? 'Update the job details and save your changes.'
              : 'Fill in the job details to create a new job posting. Job ID will be auto-generated.'}
          </p>
        </div>

        <PopupMessage
          visible={showSuccessPopup}
          title="Success"
          message={successMessage}
          type="success"
          onClose={() => setShowSuccessPopup(false)}
        />
        <PopupMessage
          visible={showErrorPopup}
          title="Error"
          message={errorMessage}
          type="error"
          onClose={() => setShowErrorPopup(false)}
        />

        {/* Info Section */}
        <div className="resume-info">
          <h3>Information</h3>
          <ul>
            <li>• All required fields are marked with <span className="required">*</span></li>
            <li>• Job ID will be auto-generated by the system</li>
            <li>• Your job posting will be securely stored</li>
            <li>• Skills should be comma-separated (e.g., JavaScript, React, Node.js)</li>
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="candidate-form">
          {/* Job Information */}
          <div className="form-section">
            <h3>Job Information</h3>
            <div className="form-grid">
              {/* Job Source Selection */}

              {/* Source Selection Popup */}
              {showSourcePopup && (
                <div className="popup-overlay" onClick={() => setShowSourcePopup(false)}>
                  <div className="popup-content" onClick={e => e.stopPropagation()}>
                    <div className="popup-header">
                      <h3>Select Job Source</h3>
                      <button className="popup-close" onClick={() => setShowSourcePopup(false)} type="button">✕</button>
                    </div>
                    <div className="popup-body">
                      <label htmlFor="sourceSearch" className="popup-label">Search by Source Name, Person, or Company</label>
                      <input
                        id="sourceSearch"
                        placeholder="Type to search..."
                        className="popup-input"
                        type="text"
                        value={sourceSearch}
                        onChange={e => setSourceSearch(e.target.value)}
                        autoFocus
                        style={{ marginBottom: 10, width: '100%' }}
                      />
                      <div className="popup-job-list">
                        <table className="popup-job-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Source Name</th>
                              <th>Contact Person</th>
                              <th>Mobile</th>
                              <th>Company</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sourceLoading ? (
                              <tr><td colSpan="5">Loading sources...</td></tr>
                            ) : (
                              (availableSources
                                .filter(src => {
                                  const q = sourceSearch.toLowerCase();
                                  return (
                                    (src.SourceName && src.SourceName.toLowerCase().includes(q)) ||
                                    (src.ContactPersonName && src.ContactPersonName.toLowerCase().includes(q)) ||
                                    (src.ContactPersonMobile && src.ContactPersonMobile.toLowerCase().includes(q)) ||
                                    (src.CompanyName && src.CompanyName.toLowerCase().includes(q))
                                  );
                                })
                                .map(src => (
                                  <tr key={src.id} className="popup-job-row" onClick={() => handleSourceSelect(src)}>
                                    <td className="popup-job-radio-cell">
                                      <input
                                        type="radio"
                                        name="selectedSource"
                                        checked={selectedSource?.id === src.id}
                                        onChange={() => handleSourceSelect(src)}
                                      />
                                    </td>
                                    <td>{src.SourceName}</td>
                                    <td>{src.ContactPersonName}</td>
                                    <td>{src.ContactPersonMobile}</td>
                                    <td>{src.CompanyName}</td>
                                  </tr>
                                )))
                            )}
                            {(!sourceLoading && availableSources.length > 0 &&
                              availableSources.filter(src => {
                                const q = sourceSearch.toLowerCase();
                                return (
                                  (src.SourceName && src.SourceName.toLowerCase().includes(q)) ||
                                  (src.ContactPersonName && src.ContactPersonName.toLowerCase().includes(q)) ||
                                  (src.ContactPersonMobile && src.ContactPersonMobile.toLowerCase().includes(q)) ||
                                  (src.CompanyName && src.CompanyName.toLowerCase().includes(q))
                                );
                              }).length === 0
                            ) && (
                              <tr><td colSpan="5" style={{ color: '#888', textAlign: 'center' }}>No sources found.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="popup-footer">
                            <button
                              type="button"
                              className="popup-btn popup-btn-primary"
                              onClick={() => {
                                // open create-source popup
                                setShowSourcePopup(false);
                                setShowCreateSourcePopup(true);
                              }}
                            >
                              Add New
                            </button>
                      {/* <button type="button" className="popup-btn popup-btn-secondary" onClick={() => setShowSourcePopup(false)}>
                        Cancel
                      </button> */}
                      <button
                        type="button"
                        className="popup-btn popup-btn-primary"
                        disabled={!selectedSource}
                        onClick={() => setShowSourcePopup(false)}
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showCreateSourcePopup && (
                <div className="popup-overlay" onClick={() => setShowCreateSourcePopup(false)}>
                  <div className="popup-content" onClick={e => e.stopPropagation()}>
                    <div className="popup-header">
                      <h3>Add New Source</h3>
                      <button className="popup-close" onClick={() => setShowCreateSourcePopup(false)} type="button">✕</button>
                    </div>
                    <div className="popup-body">
                      <div>
                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Source Name: <span className="required">*</span></label>
                            <input
                              type="text"
                              name="SourceName"
                              value={createSourceFormData.SourceName}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Enter source name"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Source Type: <span className="required">*</span></label>
                            <input
                              type="text"
                              name="SourceType"
                              value={createSourceFormData.SourceType}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Enter source type"
                              required
                            />
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Job Link:</label>
                            <input
                              type="text"
                              name="JobLink"
                              value={createSourceFormData.JobLink}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional job link"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Source Status:</label>
                            <select
                              name="SourceStatus"
                              value={createSourceFormData.SourceStatus}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                            >
                              <option value="Active">Active</option>
                              <option value="Inactive">Inactive</option>
                              <option value="Preferred">Preferred</option>
                              <option value="Blacklisted">Blacklisted</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Contact Person Name:</label>
                            <input
                              type="text"
                              name="ContactPersonName"
                              value={createSourceFormData.ContactPersonName}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional contact person"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Contact Person Mobile:</label>
                            <input
                              type="text"
                              name="ContactPersonMobile"
                              value={createSourceFormData.ContactPersonMobile}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional mobile"
                            />
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Contact Person Email:</label>
                            <input
                              type="email"
                              name="ContactPersonEmail"
                              value={createSourceFormData.ContactPersonEmail}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional email"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Contact Person LinkedIn:</label>
                            <input
                              type="text"
                              name="ContactPersonLinkedIn"
                              value={createSourceFormData.ContactPersonLinkedIn}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional LinkedIn profile"
                            />
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Secondary Contact Name:</label>
                            <input
                              type="text"
                              name="SecondaryContactName"
                              value={createSourceFormData.SecondaryContactName}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional secondary contact"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Secondary Contact Mobile:</label>
                            <input
                              type="text"
                              name="SecondaryContactMobile"
                              value={createSourceFormData.SecondaryContactMobile}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional secondary mobile"
                            />
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Secondary Contact Email:</label>
                            <input
                              type="email"
                              name="SecondaryContactEmail"
                              value={createSourceFormData.SecondaryContactEmail}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional secondary email"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">WhatsApp Group:</label>
                            <input
                              type="text"
                              name="WhatsAppGroup"
                              value={createSourceFormData.WhatsAppGroup}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional WhatsApp group"
                            />
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Company Name:</label>
                            <input
                              type="text"
                              name="CompanyName"
                              value={createSourceFormData.CompanyName}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional company name"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Company Website:</label>
                            <input
                              type="text"
                              name="CompanyWebsite"
                              value={createSourceFormData.CompanyWebsite}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional company website"
                            />
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Company LinkedIn:</label>
                            <input
                              type="text"
                              name="CompanyLinkedIn"
                              value={createSourceFormData.CompanyLinkedIn}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional company LinkedIn"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Country:</label>
                            <input
                              type="text"
                              name="Country"
                              value={createSourceFormData.Country}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional country"
                            />
                          </div>
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">State:</label>
                            <input
                              type="text"
                              name="State"
                              value={createSourceFormData.State}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional state"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">City:</label>
                            <input
                              type="text"
                              name="City"
                              value={createSourceFormData.City}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                              placeholder="Optional city"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="popup-label">Notes:</label>
                          <textarea
                            name="Notes"
                            value={createSourceFormData.Notes}
                            onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                            className="popup-input"
                            rows="3"
                            placeholder="Optional notes"
                          />
                        </div>

                        <div className="form-grid two-column-grid">
                          <div className="form-group">
                            <label className="popup-label">Last Contact Date:</label>
                            <input
                              type="date"
                              name="LastContactDate"
                              value={createSourceFormData.LastContactDate}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="popup-label">Next Follow Up Date:</label>
                            <input
                              type="date"
                              name="NextFollowUpDate"
                              value={createSourceFormData.NextFollowUpDate}
                              onChange={(e) => setCreateSourceFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                              className="popup-input"
                            />
                          </div>
                        </div>

                        <div className="popup-footer">
                          <button type="button" className="popup-btn popup-btn-primary" onClick={handleCreateSourceSubmit}>Add Source</button>
                          <button type="button" className="popup-btn popup-btn-secondary" onClick={() => setShowCreateSourcePopup(false)}>Cancel</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <PopupMessage
                visible={sourceValidationVisible}
                title={"Required fields"}
                message={sourceValidationMessage}
                onClose={() => setSourceValidationVisible(false)}
                type="error"
              />
              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  Job Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior React Developer"
                  className={`form-input ${errors.title ? "input-error" : ""}`}
                  disabled={isLoading}
                />
                {errors.title && (
                  <p className="error-text">{errors.title}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="company" className="form-label">
                  Company <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="e.g., TechCorp Inc."
                  className={`form-input ${errors.company ? "input-error" : ""}`}
                  disabled={isLoading}
                />
                {errors.company && (
                  <p className="error-text">{errors.company}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="location" className="form-label">
                  Location <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., New York, NY or Remote"
                  className={`form-input ${errors.location ? "input-error" : ""}`}
                  disabled={isLoading}
                />
                {errors.location && (
                  <p className="error-text">{errors.location}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="department" className="form-label">
                  Department
                </label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="e.g., Engineering, Marketing"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="job_type" className="form-label">
                  Job Type
                </label>
                <select
                  id="job_type"
                  name="job_type"
                  value={formData.job_type}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                >
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="freelance">Freelance</option>
                  <option value="internship">Internship</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="status" className="form-label">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                >
                  {jobStatusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="experience_required" className="form-label">
                  Experience Required
                </label>
                <input
                  type="number"
                  id="experience_required"
                  name="experience_required"
                  value={formData.experience_required}
                  onChange={handleInputChange}
                  placeholder="e.g., 3 or 3.5"
                  min="0"
                  max="30"
                  step="0.1"
                  className={`form-input ${errors.experience_required ? 'input-error' : ''}`}
                  disabled={isLoading}
                  inputMode="decimal"
                />
                {errors.experience_required && (
                  <p className="error-text">{errors.experience_required}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="budget" className="form-label">
                  Budget/Salary
                </label>
                <input
                  type="number"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  placeholder="e.g., 80000"
                  min="0"
                  max="999999"
                  step="0.01"
                  className={`form-input ${errors.budget ? 'input-error' : ''}`}
                  disabled={isLoading}
                  inputMode="numeric"
                />
                {errors.budget && (
                  <p className="error-text">{errors.budget}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="closing_on" className="form-label">
                  Application Deadline
                </label>
                <input
                  type="date"
                  id="closing_on"
                  name="closing_on"
                  value={formData.closing_on}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="remote_work" className="form-label">
                  Remote Work Available
                </label>
                <select
                  id="remote_work"
                  name="remote_work"
                  value={formData.remote_work ? "yes" : "no"}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div className="form-section">
            <h3>Job Details</h3>
            <div className="form-grid">
              <div className="form-group form-group-full">
                <label htmlFor="skill" className="form-label">
                  Required Skills <span className="required">*</span>
                </label>
                <textarea
                  id="skill"
                  name="skill"
                  value={formData.skill}
                  onChange={handleInputChange}
                  placeholder="e.g., JavaScript, React, Node.js, MongoDB"
                  className={`form-input form-textarea ${errors.skill ? "input-error" : ""}`}
                  disabled={isLoading}
                  rows="3"
                />
                {errors.skill && (
                  <p className="error-text">{errors.skill}</p>
                )}
              </div>

              <div className="form-group form-group-full">
                <label htmlFor="job_description" className="form-label">
                  Job Description
                </label>
                <textarea
                  id="job_description"
                  name="job_description"
                  value={formData.job_description}
                  onChange={handleInputChange}
                  placeholder="Detailed job description, responsibilities, requirements..."
                  className="form-input form-textarea"
                  disabled={isLoading}
                  rows="6"
                />
              </div>
            </div>
          </div>
          <div className="form-section">
            <h3>Job Source <span className="required">*</span></h3>
            <div className="form-group">
              <button type="button" className="btn btn-secondary" onClick={() => setShowSourcePopup(true)} disabled={isLoading}>
                {selectedSource ? `Selected: ${selectedSource.SourceName || selectedSource.source_type || 'Unknown source'}${selectedSource.SourceType ? ` (${selectedSource.SourceType})` : selectedSource.source_value ? ` - ${selectedSource.source_value}` : ''}` : "Select Source"}
              </button>
              {errors.source && <p className="error-text">{errors.source}</p>}
            </div>
          </div>


          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (isEditMode ? "Updating Job..." : "Creating Job...") : (isEditMode ? "Update Job" : "Create Job")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddJobPage;